import {Helmet} from 'react-helmet-async'
import {useTranslation} from 'react-i18next'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import {Button} from '@/components/ui/button'
import {Callout} from '@/components/ui/callout'
import {Card, CardContent} from '@/components/ui/card'
import {Badge} from '@/components/ui/badge'
import {ArrowLeft, Calendar, Clock} from 'lucide-react'
import {Link, Navigate, useParams, useSearchParams} from 'react-router-dom'
import {useBlogPost} from '@/hooks/useBlogPost'
import {useRelatedBlogPosts} from '@/hooks/useRelatedBlogPosts'
import {urlForImage} from '@/lib/sanity'
import {blogCategoryLabel} from '@/lib/blogCategories'
import {formatMediumDate} from '@/lib/dates'
import PortableTextRenderer from '@/components/blog/PortableTextRenderer'
import type {BlogPostSummary} from '@/types/blog'

const RelatedCard = ({article}: {article: BlogPostSummary}) => {
  const {t} = useTranslation()
  const src = article.coverImage?.asset
    ? urlForImage(article.coverImage).width(600).fit('max').auto('format').url()
    : null
  return (
    <Link to={`/blog/${article.slug}`} className="group block h-full">
      <Card className="h-full overflow-hidden hover:border-foreground/20 transition-colors">
        <div className="aspect-[16/9] bg-secondary overflow-hidden">
          {src ? (
            <img
              src={src}
              alt={article.coverImage?.alt ?? article.title}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          ) : null}
        </div>
        <CardContent className="p-5 flex flex-col gap-2">
          <div className="flex flex-wrap gap-2">
            {article.categories.slice(0, 2).map((tag) => (
              <Badge key={tag} variant="secondary" className="text-xs">
                {blogCategoryLabel(t, tag)}
              </Badge>
            ))}
          </div>
          <h3 className="font-display font-semibold text-foreground leading-snug group-hover:text-foreground/80 transition-colors">
            {article.title}
          </h3>
          <p className="text-sm text-muted-foreground line-clamp-2">{article.excerpt}</p>
        </CardContent>
      </Card>
    </Link>
  )
}

const BlogArticle = () => {
  const {t, i18n} = useTranslation()
  const {slug} = useParams<{slug: string}>()
  const [searchParams] = useSearchParams()
  const isPreview = searchParams.get('preview') === 'true'
  const previewSecret = searchParams.get('secret') ?? ''

  const {data: article, isLoading, isError} = useBlogPost(slug, {
    preview: isPreview,
    previewSecret,
  })
  const {data: related = []} = useRelatedBlogPosts({
    slug,
    categories: article?.categories,
    limit: 3,
  })

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-24 max-w-3xl">
          <div className="text-center py-16 text-muted-foreground">{t('blog.article.loading')}</div>
        </main>
        <Footer />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-24 max-w-3xl">
          <div className="text-center py-16 text-muted-foreground">
            {t('blog.article.loadError')}
          </div>
        </main>
        <Footer />
      </div>
    )
  }

  if (!article) {
    return <Navigate to="/blog" replace />
  }

  const heroSrc = article.coverImage?.asset
    ? urlForImage(article.coverImage).width(1600).fit('max').auto('format').url()
    : null

  const seoTitle = article.seoTitle || t('blog.article.metaTitleFallback', {title: article.title})

  return (
    <div className="min-h-screen bg-background">
      {/* Title (and robots) only: the Rails layout owns description/og/twitter (#674). */}
      <Helmet>
        <title>{seoTitle}</title>
        {article.noIndex ? <meta name="robots" content="noindex" /> : null}
      </Helmet>
      <Header />
      <main className="container mx-auto px-4 py-24 max-w-3xl">
        {isPreview ? (
          <Callout tone="warning" className="mb-6">
            {t('blog.article.previewBanner')}
          </Callout>
        ) : null}

        <Button variant="ghost" size="sm" className="mb-6 -ml-2" asChild>
          <Link to="/blog">
            <ArrowLeft className="w-4 h-4 mr-1" />
            {t('blog.article.allArticles')}
          </Link>
        </Button>

        <div className="aspect-[2/1] bg-secondary rounded-lg mb-8 overflow-hidden">
          {heroSrc ? (
            <img
              src={heroSrc}
              alt={article.coverImage?.alt ?? article.title}
              className="w-full h-full object-cover"
            />
          ) : null}
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {article.categories.map((tag) => (
            <Badge key={tag} variant="secondary" className="text-xs">
              {blogCategoryLabel(t, tag)}
            </Badge>
          ))}
        </div>

        <h1 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4 leading-tight">
          {article.title}
        </h1>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground mb-10 pb-6 border-b border-border">
          {article.author.slug ? (
            // A real link: the author page's only crawlable inbound link
            // from an article (#1014).
            <Link
              to={`/authors/${article.author.slug}`}
              className="text-foreground font-medium hover:underline underline-offset-2"
            >
              {article.author.name}
            </Link>
          ) : (
            <span className="text-foreground font-medium">{article.author.name}</span>
          )}
          <span>{article.author.role}</span>
          <span className="flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" /> {formatMediumDate(article.publishedAt, i18n.language)}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" /> {t('blog.minRead', {count: article.readingMinutes})}
          </span>
        </div>

        <article>
          <PortableTextRenderer value={article.body} />
        </article>

        {related.length > 0 && (
          <section className="mt-16 pt-10 border-t border-border">
            <p className="text-2xl font-display font-bold text-foreground mb-6">{t('blog.article.keepReading')}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((r) => (
                <RelatedCard key={r.slug} article={r} />
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  )
}

export default BlogArticle
